import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grow,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Logo } from '../../components/Logo';
import { brandGradient } from '../../theme/theme';
import { submitPublicInquiry } from '../../api/inquiries';
import { publicInquirySchema, PublicInquiryFormValues } from '../../validation/inquiry';

export const InquiryFormPage = () => {
  const [serverError, setServerError] = useState<string | null>(null);
  const [inquiryNumber, setInquiryNumber] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PublicInquiryFormValues>({ resolver: zodResolver(publicInquirySchema) });

  const onSubmit = async (values: PublicInquiryFormValues) => {
    setServerError(null);
    try {
      const result = await submitPublicInquiry({
        ...values,
        email: values.email || undefined,
        countryOfInterest: values.countryOfInterest || undefined,
        courseOfInterest: values.courseOfInterest || undefined,
        message: values.message || undefined,
      });
      setInquiryNumber(result.inquiryNumber);
      reset();
    } catch (err) {
      setServerError(
        isAxiosError(err) && err.response?.status === 429
          ? 'Too many submissions from this network — please try again later.'
          : 'Something went wrong. Please try again.',
      );
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '1 1 46%',
          position: 'relative',
          background: brandGradient,
          color: '#fff',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.94)', borderRadius: 2, px: 2, py: 1 }}>
          <Logo height={32} />
        </Box>

        <Box>
          <Typography variant="h4" sx={{ mb: 1.5 }}>
            Start your journey abroad
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 420 }}>
            Tell us where you'd like to study and one of our counselors will reach out with the
            right next steps — no account needed.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: '1 1 54%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, py: 6 }}>
        <Grow in timeout={400}>
          <Box sx={{ width: '100%', maxWidth: 460 }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
              <Logo height={44} />
            </Box>

            {inquiryNumber ? (
              <Stack spacing={2} alignItems="center" textAlign="center">
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
                <Typography variant="h5">Thanks for reaching out</Typography>
                <Typography variant="body2" color="text.secondary">
                  We've received your inquiry (reference {inquiryNumber}) and a counselor will be in
                  touch soon.
                </Typography>
                <Button variant="outlined" onClick={() => setInquiryNumber(null)}>
                  Submit another inquiry
                </Button>
              </Stack>
            ) : (
              <>
                <Typography variant="h5" sx={{ mb: 0.5 }}>
                  Tell us about your plans
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Share a few details and we'll follow up with study options that fit.
                </Typography>

                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                  <Stack spacing={2.5}>
                    {serverError && <Alert severity="error">{serverError}</Alert>}

                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="First name"
                        fullWidth
                        autoFocus
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        {...register('firstName')}
                      />
                      <TextField
                        label="Last name"
                        fullWidth
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        {...register('lastName')}
                      />
                    </Stack>

                    <TextField
                      label="Phone number"
                      fullWidth
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      {...register('phone')}
                    />

                    <TextField
                      label="Email (optional)"
                      type="email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      {...register('email')}
                    />

                    <Stack direction="row" spacing={2}>
                      <TextField label="Country of interest" fullWidth {...register('countryOfInterest')} />
                      <TextField label="Course of interest" fullWidth {...register('courseOfInterest')} />
                    </Stack>

                    <TextField label="Message (optional)" fullWidth multiline minRows={3} {...register('message')} />

                    {/* Honeypot: hidden from sighted and screen-reader users alike; real
                        visitors never see or fill it, bots that fill every field do. */}
                    <Box
                      aria-hidden="true"
                      sx={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}
                    >
                      <TextField
                        label="Website"
                        tabIndex={-1}
                        autoComplete="off"
                        {...register('website')}
                      />
                    </Box>

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                      fullWidth
                      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                    >
                      {isSubmitting ? 'Submitting…' : 'Submit inquiry'}
                    </Button>

                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Already have an account? <Link component={RouterLink} to="/login">Sign in</Link>
                    </Typography>
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </Grow>
      </Box>
    </Box>
  );
};
